import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentArrowDownIcon, DocumentArrowUpIcon, ClipboardDocumentIcon, EyeIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { University, Campus, Course, Session } from '../../types/UniversityPortal';
import { universityPortalApi } from '../../services/universityPortalApi';

interface BatchCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  universities: University[];
  campuses: Campus[];
}

interface BatchData {
  sessions: any[];
  universities: any[];
  campuses: any[];
  courses: any[];
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

const BatchCreationModal: React.FC<BatchCreationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  universities,
  campuses
}) => {
  const [activeTab, setActiveTab] = useState<'sessions' | 'universities' | 'campuses' | 'courses'>('sessions');
  const [rawData, setRawData] = useState('');
  const [parsedData, setParsedData] = useState<BatchData>({
    sessions: [],
    universities: [],
    campuses: [],
    courses: []
  });
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [missingUniversities, setMissingUniversities] = useState<string[]>([]);
  const [creationResults, setCreationResults] = useState<{
    created: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  // Template data for each type
  const templates = {
    sessions: `Code,Name,Start Date,End Date,Active
SPRING2024,Spring 2024,2024-01-15,2024-05-15,true
FALL2024,Fall 2024,2024-08-20,2024-12-15,true
SUMMER2024,Summer 2024,2024-06-01,2024-08-10,false`,

    universities: `Name,Description,Website,Email,Phone,Established Year,Active
Harvard University,World-renowned research university,https://harvard.edu,info@harvard.edu,+1-617-495-1000,1636,true
MIT,Massachusetts Institute of Technology,https://mit.edu,info@mit.edu,+1-617-253-1000,1861,true
Stanford University,Leading research university,https://stanford.edu,info@stanford.edu,+1-650-723-2300,1885,true`,

    campuses: `University Name,Campus Name,Country,City,Address,Postal Code,Phone,Email,Main Campus,Active
Harvard University,Harvard Yard,USA,Cambridge,1350 Massachusetts Ave,02138,+1-617-495-1000,cambridge@harvard.edu,true,true
MIT,Main Campus,USA,Cambridge,77 Massachusetts Ave,02139,+1-617-253-1000,info@mit.edu,true,true
Stanford University,Main Campus,USA,Stanford,450 Serra Mall,94305,+1-650-723-2300,info@stanford.edu,true,true`,

    courses: `University Name,Campus Name,Course Name,Code,Description,Credits,Duration Months,Active
Harvard University,Harvard Yard,Computer Science 101,CS101,Introduction to Computer Science,3,4,true
MIT,Main Campus,Data Structures and Algorithms,6.006,Advanced algorithms course,4,4,true
Stanford University,Main Campus,Machine Learning,CS229,Machine learning fundamentals,3,4,true`
  };

  const fieldMappings = {
    sessions: ['code', 'name', 'start date', 'end date', 'active'],
    universities: ['name', 'description', 'website', 'email', 'phone', 'established year', 'active'],
    campuses: ['university name', 'campus name', 'country', 'city', 'address', 'postal code', 'phone', 'email', 'main campus', 'active'],
    courses: ['university name', 'campus name', 'course name', 'code', 'description', 'credits', 'duration months', 'active']
  };

  const requiredFields = {
    sessions: ['code', 'name'],
    universities: ['name'],
    campuses: ['university name', 'campus name', 'country', 'city'],
    courses: ['university name', 'course name']
  };

  // Parse CSV data
  const parseCSVData = (csvText: string, type: keyof BatchData): any[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) continue;

      const row: any = {};
      headers.forEach((header, index) => {
        let value: any = values[index];
        
        // Handle boolean values
        if (value.toLowerCase() === 'true') {
          value = true;
        } else if (value.toLowerCase() === 'false') {
          value = false;
        } else if (!isNaN(Number(value)) && value !== '') {
          // Handle numeric values
          value = Number(value);
        }

        row[header] = value;
      });

      data.push(row);
    }

    return data;
  };

  // Check for missing universities and campuses
  const checkMissingUniversities = (data: any[], type: keyof BatchData): string[] => {
    if (type !== 'campuses' && type !== 'courses') return [];
    
    const missing: string[] = [];
    const universityField = type === 'campuses' ? 'university name' : 'university name';
    
    data.forEach((item) => {
      if (item[universityField]) {
        const universityExists = universities.some(u => 
          u.name.toLowerCase().includes(item[universityField].toLowerCase()) ||
          item[universityField].toLowerCase().includes(u.name.toLowerCase())
        );
        if (!universityExists && !missing.includes(item[universityField])) {
          missing.push(item[universityField]);
        }
      }

      // For courses, also check if campus exists
      if (type === 'courses' && item['campus name'] && item[universityField]) {
        const university = universities.find(u => 
          u.name.toLowerCase().includes(item[universityField].toLowerCase()) ||
          item[universityField].toLowerCase().includes(u.name.toLowerCase())
        );
        
        if (university) {
          const campusExists = campuses.some((c: Campus) => 
            c.universityId === university.id && (
              c.name.toLowerCase().includes(item['campus name'].toLowerCase()) ||
              item['campus name'].toLowerCase().includes(c.name.toLowerCase())
            )
          );
          if (!campusExists && !missing.includes(`${item['campus name']} (${item[universityField]})`)) {
            missing.push(`${item['campus name']} (${item[universityField]})`);
          }
        }
      }
    });
    
    return missing;
  };

  // Validate data
  const validateData = (data: any[], type: keyof BatchData): ValidationError[] => {
    const errors: ValidationError[] = [];
    const required = requiredFields[type];

    data.forEach((item, index) => {
      // Check required fields
      required.forEach(field => {
        if (!item[field] || item[field] === '') {
          errors.push({
            row: index + 2, // +2 because CSV is 1-indexed and has header
            field,
            message: `${field} is required`
          });
        }
      });

      // Type-specific validation
      if (type === 'sessions') {
        if (item.code && typeof item.code !== 'string') {
          errors.push({
            row: index + 2,
            field: 'code',
            message: 'Code must be a string'
          });
        }
      }

      if (type === 'universities') {
        if (item.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email)) {
          errors.push({
            row: index + 2,
            field: 'email',
            message: 'Invalid email format'
          });
        }
        if (item.website && !item.website.startsWith('http')) {
          errors.push({
            row: index + 2,
            field: 'website',
            message: 'Website must start with http'
          });
        }
      }

      if (type === 'campuses' || type === 'courses') {
        const universityField = type === 'campuses' ? 'university name' : 'university name';
        if (item[universityField]) {
          const universityExists = universities.some(u => 
            u.name.toLowerCase().includes(item[universityField].toLowerCase()) ||
            item[universityField].toLowerCase().includes(u.name.toLowerCase())
          );
          if (!universityExists) {
            errors.push({
              row: index + 2,
              field: universityField,
              message: `University "${item[universityField]}" not found. Please create it first or check the name.`
            });
          }
        }
      }
    });

    return errors;
  };

  // Process and validate data
  const processData = () => {
    if (!rawData.trim()) {
      setParsedData({ sessions: [], universities: [], campuses: [], courses: [] });
      setValidationErrors([]);
      setMissingUniversities([]);
      setCreationResults(null);
      return;
    }

    setIsProcessing(true);
    
    try {
      const parsed = parseCSVData(rawData, activeTab);
      const errors = validateData(parsed, activeTab);
      const missing = checkMissingUniversities(parsed, activeTab);
      
      setParsedData(prev => ({
        ...prev,
        [activeTab]: parsed
      }));
      setValidationErrors(errors);
      setMissingUniversities(missing);
      setCreationResults(null);
    } catch (error) {
      setValidationErrors([{
        row: 0,
        field: 'general',
        message: 'Error parsing data. Please check the format.'
      }]);
      setMissingUniversities([]);
      setCreationResults(null);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle data input change
  const handleDataChange = (value: string) => {
    setRawData(value);
  };

  // Process data when raw data or active tab changes
  useEffect(() => {
    if (rawData.trim()) {
      processData();
    }
  }, [rawData, activeTab, universities]);

  // Load template
  const loadTemplate = () => {
    setRawData(templates[activeTab]);
  };

  // Export template
  const exportTemplate = () => {
    const blob = new Blob([templates[activeTab]], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(templates[activeTab]);
      alert('Template copied to clipboard!');
    } catch (err) {
      alert('Failed to copy to clipboard');
    }
  };

  // Create batch data
  const handleCreateBatch = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before creating');
      return;
    }

    setIsCreating(true);
    const results = {
      created: 0,
      skipped: 0,
      errors: [] as string[]
    };

    try {
      const data = parsedData[activeTab];

      for (const item of data) {
        try {
          if (activeTab === 'sessions') {
            await universityPortalApi.createSession({
              code: item.code as string,
              name: item.name as string,
              startDate: item['start date'] as string,
              endDate: item['end date'] as string,
              isActive: item.active as boolean
            });
            results.created++;
          } else if (activeTab === 'universities') {
            await universityPortalApi.createUniversity({
              name: item.name as string,
              description: item.description as string,
              website: item.website as string,
              email: item.email as string,
              phone: item.phone as string,
              establishedYear: item['established year'] as number,
              isActive: item.active as boolean
            });
            results.created++;
          } else if (activeTab === 'campuses') {
            // Find university by name matching
            const university = universities.find(u => 
              u.name.toLowerCase().includes((item['university name'] as string).toLowerCase()) ||
              (item['university name'] as string).toLowerCase().includes(u.name.toLowerCase())
            );
            
            if (university) {
              await universityPortalApi.createCampus({
                universityId: university.id,
                name: item['campus name'] as string,
                country: item.country as string,
                city: item.city as string,
                address: item.address as string,
                postalCode: item['postal code'] as string,
                phone: item.phone as string,
                email: item.email as string,
                isMainCampus: item['main campus'] as boolean,
                isActive: item.active as boolean
              });
              results.created++;
            } else {
              results.skipped++;
              results.errors.push(`Skipped campus "${item['campus name']}" - University "${item['university name']}" not found`);
            }
          } else if (activeTab === 'courses') {
            // Find university by name matching
            const university = universities.find(u => 
              u.name.toLowerCase().includes((item['university name'] as string).toLowerCase()) ||
              (item['university name'] as string).toLowerCase().includes(u.name.toLowerCase())
            );
            
            if (university) {
              // Find campus by name matching within the university
              let campusId = undefined;
              if (item['campus name']) {
                const campus = campuses.find((c: Campus) => 
                  c.universityId === university.id && (
                    c.name.toLowerCase().includes((item['campus name'] as string).toLowerCase()) ||
                    (item['campus name'] as string).toLowerCase().includes(c.name.toLowerCase())
                  )
                );
                if (campus) {
                  campusId = campus.id;
                } else {
                  results.skipped++;
                  results.errors.push(`Skipped course "${item['course name']}" - Campus "${item['campus name']}" not found for university "${item['university name']}"`);
                  continue;
                }
              }

              await universityPortalApi.createCourse({
                universityId: university.id,
                campusId: campusId,
                name: item['course name'] as string,
                code: item.code as string,
                description: item.description as string,
                credits: item.credits as number,
                durationMonths: item['duration months'] as number,
                isActive: item.active as boolean
              });
              results.created++;
            } else {
              results.skipped++;
              results.errors.push(`Skipped course "${item['course name']}" - University "${item['university name']}" not found`);
            }
          }
        } catch (error) {
          results.skipped++;
          results.errors.push(`Error creating ${activeTab.slice(0, -1)}: ${error}`);
          console.error(`Error creating ${activeTab.slice(0, -1)}:`, error);
        }
      }

      setCreationResults(results);
      
      if (results.skipped > 0) {
        // Don't close modal if there were skipped items
        console.log('Batch creation completed with some items skipped:', results);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Batch creation error:', error);
      results.errors.push(`Batch creation failed: ${error}`);
      setCreationResults(results);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Batch Create {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {(['sessions', 'universities', 'campuses', 'courses'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          {/* Template Actions */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex space-x-3">
              <button
                onClick={loadTemplate}
                className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                Load Template
              </button>
              <button
                onClick={copyToClipboard}
                className="flex items-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                Copy Template
              </button>
              <button
                onClick={exportTemplate}
                className="flex items-center px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Template
              </button>
            </div>
            
            <div className="text-sm text-gray-500">
              Paste CSV data or use template below
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSV Data Input
              </label>
              <textarea
                value={rawData}
                onChange={(e) => handleDataChange(e.target.value)}
                placeholder="Paste your CSV data here or use the template..."
                className="w-full h-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              />
              
              {isProcessing && (
                <div className="mt-2 text-sm text-blue-600">
                  Processing data...
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview ({parsedData[activeTab].length} items)
              </label>
              
              <div className="border border-gray-300 rounded-lg h-96 overflow-auto">
                {parsedData[activeTab].length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No data to preview. Enter CSV data to see preview.
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {fieldMappings[activeTab].map((field) => (
                          <th key={field} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData[activeTab].slice(0, 10).map((item, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {fieldMappings[activeTab].map((field) => (
                            <td key={field} className="px-3 py-2 text-xs text-gray-900">
                              {String((item as any)[field] || '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {parsedData[activeTab].length > 10 && (
                <div className="mt-2 text-sm text-gray-500">
                  Showing first 10 of {parsedData[activeTab].length} items
                </div>
              )}
            </div>
          </div>

          {/* Missing Universities Warning */}
          {missingUniversities.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center mb-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <h4 className="text-sm font-medium text-yellow-800">Missing Universities</h4>
              </div>
              <div className="text-sm text-yellow-700 mb-3">
                The following universities and campuses are referenced in your data but don't exist in your database:
              </div>
              <div className="text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  {missingUniversities.map((university, index) => (
                    <li key={index} className="font-medium">"{university}"</li>
                  ))}
                </ul>
              </div>
              <div className="mt-3 text-sm text-yellow-700">
                <strong>Note:</strong> Items referencing these universities or campuses will be skipped during creation. 
                Please create these universities and campuses first or check the names for typos.
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
              </div>
              <div className="text-sm text-red-700">
                {validationErrors.map((error, index) => (
                  <div key={index}>
                    Row {error.row}, {error.field}: {error.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Creation Results */}
          {creationResults && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="text-sm font-medium text-blue-800">Creation Results</h4>
              </div>
              <div className="text-sm text-blue-700 mb-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Created:</span> {creationResults.created} items
                  </div>
                  <div>
                    <span className="font-medium">Skipped:</span> {creationResults.skipped} items
                  </div>
                </div>
              </div>
              {creationResults.errors.length > 0 && (
                <div className="text-sm text-blue-700">
                  <div className="font-medium mb-2">Skipped Items:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {creationResults.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {parsedData[activeTab].length > 0 && validationErrors.length === 0 && missingUniversities.length === 0 && !creationResults && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-800">
                  Data validation passed! Ready to create {parsedData[activeTab].length} {activeTab}.
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateBatch}
              disabled={parsedData[activeTab].length === 0 || validationErrors.length > 0 || isCreating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : `Create ${parsedData[activeTab].length} ${activeTab}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchCreationModal;
